/**
 * Aircraft on Teslyn Cloud's maps, drawn as Teslyn Field draws them (`apps/field/src/map/LeafletMap.tsx` and
 * `trailLayer.ts`; Diurnal parity 03-022, 03-029, 03-034; MB, 27 Oct 2026: the cloud maps should show public air
 * traffic as Teslyn Field does): each trail dashed in its source's trail colour at Teslyn Field's default trail
 * opacity and weight (`DEFAULT_TRAIL_STYLE`, dashes 2.5 times the weight), the aircraft turned to its heading in its
 * source's colour (`aircraftIcon`), its label always shown above it ("VH-ABC · AvPlan", with SOS, lost tracking, RFDS
 * or NAFC in words, never colour alone) and its take-off, flight time and landing below it. Used by the Operations
 * Hub and the project map (staff: public air traffic; clients: their own aircraft, no tracker names). Colours are
 * tokens; text reaches the map only as text nodes.
 */
import L from 'leaflet';
import {
  DEFAULT_TRAIL_STYLE,
  flightStatusLabel,
  trackedAircraftLabel,
  type AircraftColours,
  type AircraftPosition,
  type TrailPoint,
  type TrailStyle,
} from '@teslyn/domain';
import { aircraftIcon } from './aircraftIcon.ts';
import { colourOf, css, textNode } from './leaflet.ts';

export interface AircraftLayerInput {
  readonly aircraft: readonly AircraftPosition[];
  readonly trails: ReadonlyMap<string, readonly TrailPoint[]>;
  readonly colours: ReadonlyMap<string, AircraftColours>;
}

/** Leaflet path options of a trail: Teslyn Field's (`trailPathOptions`, not highlighted). */
export function trailOptions(style: TrailStyle = DEFAULT_TRAIL_STYLE): {
  weight: number;
  opacity: number;
  dashArray: string;
} {
  const w = style.weight;
  return { weight: w, opacity: style.opacity, dashArray: `${w * 2.5} ${w * 2.5}` };
}

/** The words above an aircraft, as Teslyn Field's: the domain's `trackedAircraftLabel` (Teslyn Mobile's too). */
export function aircraftLabel(a: AircraftPosition): string {
  return trackedAircraftLabel(a);
}

/**
 * Draw the aircraft into `trails` (one polyline each) and `markers` (the icon, its label and its flight line). The
 * caller clears both first.
 */
export function drawAircraft(
  trails: L.LayerGroup,
  markers: L.LayerGroup,
  t: AircraftLayerInput,
  o: {
    readonly offsetH: number;
    readonly renderer?: L.Renderer;
    readonly trailPane?: string;
    /**
     * The take-off, flight time and landing under an own aircraft (true unless said otherwise). The client portal
     * map passes false: its list under the map has the flight line (client-area.md §11.1), so it is not shown twice.
     */
    readonly flightLines?: boolean;
    /** A click on a public aircraft (its planned route on a staff map, R160); none: markers do nothing. */
    readonly onPublicClick?: (a: AircraftPosition) => void;
  },
): void {
  const size = Number.parseFloat(css('map-aircraft-size')) || 28;
  const path = trailOptions();
  for (const a of t.aircraft) {
    const colours = t.colours.get(a.id);
    const trail = t.trails.get(a.id) ?? [];
    if (trail.length > 1) {
      trails.addLayer(
        L.polyline(
          trail.map((x) => [x.lat, x.lon] as L.LatLngTuple),
          {
            ...path,
            color: colourOf(colours?.trail ?? 'accent'),
            interactive: false,
            ...(o.renderer ? { renderer: o.renderer } : {}),
            ...(o.trailPane ? { pane: o.trailPane } : {}),
          },
        ),
      );
    }
    const icon = aircraftIcon(a, colours?.icon ?? 'accent');
    const marker = L.marker([a.lat, a.lng], {
      icon: L.divIcon({
        className: icon.className,
        html: icon.html,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      }),
      keyboard: false,
      zIndexOffset: 1000,
      bubblingMouseEvents: false,
    }).bindTooltip(textNode(aircraftLabel(a)), {
      permanent: true,
      direction: 'top',
      offset: [0, -size / 2],
    });
    const click = o.onPublicClick;
    if (click && a.fleet === false) marker.on('click', () => click(a));
    markers.addLayer(marker);
    // take-off, flight time and landing under the aircraft (03-034; the organisation's own aircraft only)
    const flight = a.flight && o.flightLines !== false ? flightStatusLabel(a.flight, o.offsetH) : '';
    if (flight)
      markers.addLayer(
        L.tooltip({
          permanent: true,
          direction: 'bottom',
          className: 'map-label map-aircraft__flight',
          offset: [0, size / 2],
        })
          .setLatLng([a.lat, a.lng])
          .setContent(textNode(flight)),
      );
  }
}
